/**
 * ðŸŽ¯ PROJECT ROADMAP - Roadmap Visual Interativo
 * 
 * Componente de roadmap tipo Gantt/Timeline para visualizar
 * kaizens planejados, milestones e progresso do projeto.
 */

import React, { useState, useMemo } from 'react';
import {
  Calendar,
  Plus,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Trash2,
  Flag,
  Wrench,
  GraduationCap,
  ClipboardCheck,
  Eye,
  CheckCircle2,
  Clock,
  Pause,
  XCircle,
  MoreHorizontal,
} from 'lucide-react';
import { RoadmapItem, generateId } from '../../types/consultingProject';

interface ProjectRoadmapProps {
  items: RoadmapItem[];
  onAddItem: (item: Partial<RoadmapItem>) => void;
  onUpdateItem: (id: string, updates: Partial<RoadmapItem>) => void;
  onDeleteItem: (id: string) => void;
  onClickItem?: (item: RoadmapItem) => void;
  readOnly?: boolean;
}

const TYPE_CONFIG = {
  kaizen: { icon: Wrench, label: 'Melhoria Ativa', color: '#10B981' },
  milestone: { icon: Flag, label: 'Marco', color: '#8B5CF6' },
  training: { icon: GraduationCap, label: 'Treinamento', color: '#3B82F6' },
  audit: { icon: ClipboardCheck, label: 'Auditoria', color: '#F59E0B' },
  review: { icon: Eye, label: 'Revisão', color: '#6366F1' },
};

const STATUS_CONFIG = {
  planned: { icon: Clock, label: 'Planejado', color: '#94A3B8' },
  in_progress: { icon: Clock, label: 'Em Andamento', color: '#3B82F6' },
  completed: { icon: CheckCircle2, label: 'Concluído', color: '#10B981' },
  postponed: { icon: Pause, label: 'Adiado', color: '#F59E0B' },
  cancelled: { icon: XCircle, label: 'Cancelado', color: '#EF4444' },
};

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export const ProjectRoadmap: React.FC<ProjectRoadmapProps> = ({
  items,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  onClickItem,
  readOnly = false,
}) => {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<RoadmapItem | null>(null);
  const [contextMenu, setContextMenu] = useState<{ item: RoadmapItem; x: number; y: number } | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    area: '',
    type: 'kaizen' as RoadmapItem['type'],
    plannedStart: '',
    plannedEnd: '',
    team: '',
    expectedGains: '',
  });

  // Agrupar itens por área (swimlanes)
  const lanes = useMemo(() => {
    const grouped: Record<string, RoadmapItem[]> = {};
    items.forEach(item => {
      const area = item.area || 'Geral';
      if (!grouped[area]) grouped[area] = [];
      grouped[area].push(item);
    });
    // Ordenar itens dentro de cada lane
    Object.values(grouped).forEach(laneItems => {
      laneItems.sort((a, b) => new Date(a.plannedStart).getTime() - new Date(b.plannedStart).getTime());
    });
    return grouped;
  }, [items]);

  const getMonthPosition = (dateStr: string): number => {
    const date = new Date(dateStr);
    if (date.getFullYear() !== currentYear) {
      return date.getFullYear() < currentYear ? -1 : 13;
    }
    return date.getMonth() + (date.getDate() / 30);
  };

  const getBarStyle = (item: RoadmapItem) => {
    const start = Math.max(0, getMonthPosition(item.plannedStart));
    const end = Math.min(12, getMonthPosition(item.plannedEnd));
    const width = Math.max(0.5, end - start);
    const left = (start / 12) * 100;
    const barWidth = (width / 12) * 100;

    const typeConfig = TYPE_CONFIG[item.type];
    const statusConfig = STATUS_CONFIG[item.status];

    return {
      left: `${left}%`,
      width: `${barWidth}%`,
      backgroundColor: item.status === 'completed'
        ? statusConfig.color
        : item.status === 'cancelled'
          ? '#94A3B8'
          : typeConfig.color,
      opacity: item.status === 'cancelled' ? 0.4 : item.status === 'postponed' ? 0.6 : 1,
    };
  };

  const handleSubmit = () => {
    if (!formData.title || !formData.plannedStart || !formData.plannedEnd) return;

    const itemData: Partial<RoadmapItem> = {
      title: formData.title,
      description: formData.description,
      area: formData.area || 'Geral',
      type: formData.type,
      plannedStart: formData.plannedStart,
      plannedEnd: formData.plannedEnd,
      team: formData.team.split(',').map(t => t.trim()).filter(Boolean),
      expectedGains: formData.expectedGains,
    };

    if (editingItem) {
      onUpdateItem(editingItem.id, itemData);
    } else {
      onAddItem(itemData);
    }

    setFormData({ title: '', description: '', area: '', type: 'kaizen', plannedStart: '', plannedEnd: '', team: '', expectedGains: '' });
    setShowForm(false);
    setEditingItem(null);
  };

  const handleEdit = (item: RoadmapItem) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      area: item.area,
      type: item.type,
      plannedStart: item.plannedStart.substring(0, 10),
      plannedEnd: item.plannedEnd.substring(0, 10),
      team: item.team.join(', '),
      expectedGains: item.expectedGains || '',
    });
    setShowForm(true);
    setContextMenu(null);
  };

  const currentMonth = new Date().getMonth();
  const isCurrentYear = new Date().getFullYear() === currentYear;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentYear(y => y - 1)}
              className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-lg font-bold text-slate-800 dark:text-slate-200 min-w-[60px] text-center">
              {currentYear}
            </span>
            <button
              onClick={() => setCurrentYear(y => y + 1)}
              className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {!readOnly && (
          <button
            onClick={() => { setShowForm(true); setEditingItem(null); }}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus size={14} />
            Adicionar
          </button>
        )}
      </div>

      {/* Legenda */}
      <div className="flex flex-wrap gap-3 text-xs">
        {Object.entries(TYPE_CONFIG).map(([key, config]) => (
          <div key={key} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: config.color }} />
            <span className="text-slate-600 dark:text-slate-400">{config.label}</span>
          </div>
        ))}
      </div>

      {/* Timeline Grid */}
      <div className="border border-slate-200 dark:border-slate-700 rounded overflow-hidden bg-white dark:bg-slate-800/50">
        {/* Month headers */}
        <div className="grid grid-cols-12 border-b border-slate-200 dark:border-slate-700">
          {MONTHS.map((month, i) => (
            <div
              key={month}
              className={`
                py-2 text-center text-xs font-medium border-r border-slate-100 dark:border-slate-700/50
                ${isCurrentYear && i === currentMonth
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-bold'
                  : 'text-slate-500 dark:text-slate-500'
                }
              `}
            >
              {month}
            </div>
          ))}
        </div>

        {/* Swimlanes */}
        {Object.keys(lanes).length === 0 ? (
          <div className="py-16 text-center text-slate-400 dark:text-slate-600">
            <Calendar size={40} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm">Nenhum item no roadmap</p>
            <p className="text-xs mt-1">Adicione melhorias, marcos e treinamentos ao seu projeto</p>
          </div>
        ) : (
          Object.entries(lanes).map(([area, laneItems]) => (
            <div key={area} className="border-b border-slate-100 dark:border-slate-700/50 last:border-0">
              {/* Area label */}
              <div className="flex">
                <div className="w-32 min-w-[128px] flex-shrink-0 px-3 py-2 bg-slate-50 dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700">
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate block">
                    {area}
                  </span>
                  <span className="text-[10px] text-slate-400 dark:text-slate-500">
                    {laneItems.length} {laneItems.length === 1 ? 'item' : 'itens'}
                  </span>
                </div>
                
                {/* Timeline bars */}
                <div className="flex-1 relative min-h-[48px]">
                  {/* Month grid lines */}
                  <div className="absolute inset-0 grid grid-cols-12">
                    {MONTHS.map((_, i) => (
                      <div
                        key={i}
                        className={`border-r border-slate-50 dark:border-slate-700/30 ${
                          isCurrentYear && i === currentMonth ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''
                        }`}
                      />
                    ))}
                  </div>

                  {/* Current month indicator */}
                  {isCurrentYear && (
                    <div
                      className="absolute top-0 bottom-0 w-[2px] bg-blue-500 dark:bg-blue-400 z-10"
                      style={{
                        left: `${((currentMonth + new Date().getDate() / 30) / 12) * 100}%`,
                      }}
                    />
                  )}

                  {/* Bars */}
                  {laneItems.map((item, idx) => {
                    const barStyle = getBarStyle(item);
                    const TypeIcon = TYPE_CONFIG[item.type].icon;
                    return (
                      <div
                        key={item.id}
                        className="absolute h-7 flex items-center rounded-md px-2 cursor-pointer hover:brightness-110 hover:shadow-md transition-all group z-20"
                        style={{
                          ...barStyle,
                          top: `${idx * 32 + 8}px`,
                        }}
                        onClick={() => onClickItem?.(item)}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          if (!readOnly) setContextMenu({ item, x: e.clientX, y: e.clientY });
                        }}
                        title={`${item.title}\n${STATUS_CONFIG[item.status].label}`}
                      >
                        <TypeIcon size={12} className="text-white flex-shrink-0" />
                        <span className="text-[10px] font-medium text-white ml-1 truncate">
                          {item.title}
                        </span>
                        {item.status === 'completed' && (
                          <CheckCircle2 size={10} className="text-white/80 ml-auto flex-shrink-0" />
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Expand height for multiple bars */}
                  <div style={{ height: `${Math.max(48, laneItems.length * 32 + 16)}px` }} />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <>
          <div className="fixed inset-0 z-50" onClick={() => setContextMenu(null)} />
          <div
            className="fixed z-50 bg-white dark:bg-slate-800 rounded shadow-xl border border-slate-200 dark:border-slate-700 py-1 min-w-[160px]"
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            <button
              onClick={() => handleEdit(contextMenu.item)}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <Edit3 size={12} /> Editar
            </button>
            {(['planned', 'in_progress', 'completed', 'postponed'] as const).map(status => (
              contextMenu.item.status !== status && (
                <button
                  key={status}
                  onClick={() => {
                    onUpdateItem(contextMenu.item.id, { status });
                    setContextMenu(null);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  {React.createElement(STATUS_CONFIG[status].icon, { size: 12, style: { color: STATUS_CONFIG[status].color } })}
                  {STATUS_CONFIG[status].label}
                </button>
              )
            ))}
            <hr className="my-1 border-slate-200 dark:border-slate-700" />
            <button
              onClick={() => {
                onDeleteItem(contextMenu.item.id);
                setContextMenu(null);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 size={12} /> Excluir
            </button>
          </div>
        </>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded shadow-2xl w-full max-w-lg p-6 mx-4">
            <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 mb-4">
              {editingItem ? 'Editar Item' : 'Novo Item do Roadmap'}
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Título *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData(f => ({ ...f, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Ex: Melhoria Layout - Almoxarifado"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Tipo</label>
                  <select
                    value={formData.type}
                    onChange={e => setFormData(f => ({ ...f, type: e.target.value as RoadmapItem['type'] }))}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                  >
                    {Object.entries(TYPE_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Área</label>
                  <input
                    type="text"
                    value={formData.area}
                    onChange={e => setFormData(f => ({ ...f, area: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                    placeholder="Ex: Produção, Logística"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Data Início *</label>
                  <input
                    type="date"
                    value={formData.plannedStart}
                    onChange={e => setFormData(f => ({ ...f, plannedStart: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Data Fim *</label>
                  <input
                    type="date"
                    value={formData.plannedEnd}
                    onChange={e => setFormData(f => ({ ...f, plannedEnd: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 resize-none"
                  rows={2}
                  placeholder="Descrição do item..."
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Equipe (separado por vírgula)</label>
                <input
                  type="text"
                  value={formData.team}
                  onChange={e => setFormData(f => ({ ...f, team: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                  placeholder="João, Maria, Carlos"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Ganhos Esperados</label>
                <input
                  type="text"
                  value={formData.expectedGains}
                  onChange={e => setFormData(f => ({ ...f, expectedGains: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                  placeholder="Ex: Redução de 30% no lead time"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => { setShowForm(false); setEditingItem(null); }}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={!formData.title || !formData.plannedStart || !formData.plannedEnd}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {editingItem ? 'Salvar' : 'Adicionar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
